import { useContext, useEffect, useRef, useState } from 'react';

import { matchRoutes, useLocation } from 'react-router';

import { PermissionsContext, authReducer } from '@bitstopco/bitstop-theme';

import { ROUTES_PERMISSION } from '@/constants/app/views';
import { useAppSelector } from '@/store';

const {
  selectors: { userInfo, userInfoStatus },
} = authReducer;

const useViewPermissions = () => {
  const view = useRef<string>();
  const [isLoading, setIsLoading] = useState(true);

  const reactLocation = useLocation();

  const { isAllowedTo } = useContext(PermissionsContext);
  const userData = useAppSelector(userInfo);
  const userDataStatus = useAppSelector(userInfoStatus);

  useEffect(() => {
    // Don't evaluate route permissions until userinfo has actually finished loading. Otherwise
    // this can race the async userinfo fetch: on first mount userData is still undefined, the
    // permission/operatorId checks below see empty data and redirect away, and — because
    // view.current is only ever set once per pathname — that wrong decision is never revisited
    // once the real userinfo data arrives.
    if (userDataStatus === 'loading') return;

    if (view.current !== reactLocation.pathname) {
      view.current = reactLocation.pathname;

      (async () => {
        try {
          setIsLoading(true);

          const matchedRoute = matchRoutes(
            ROUTES_PERMISSION.map(({ path }) => ({ path })),
            reactLocation,
          );

          const matchedPath = matchedRoute?.[0]?.route?.path;
          if (matchedPath) {
            const routePermission = ROUTES_PERMISSION.find(({ path }) => path === matchedPath);

            if (routePermission?.permission) {
              const isAllowed = await isAllowedTo(routePermission.permission, 'AND');

              if (!isAllowed) {
                location.href = location.origin;
                return;
              }
            }

            if (routePermission?.operatorId !== undefined) {
              if (userData?.context?.operator_id !== routePermission.operatorId) {
                location.href = location.origin;
                return;
              }
            }
          }

          setIsLoading(false);
        } catch {
          setIsLoading(false);
        }
      })();
    }
  }, [reactLocation, userData, userDataStatus]);

  return { isLoading };
};

export default useViewPermissions;
